'use client';

import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar';
import { cva } from 'class-variance-authority';
import { Check, Pencil, Plus, Star, User } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

// ============================================================================
// Context for AvatarGroup
// ============================================================================

interface AvatarGroupContextValue {
  size: AvatarSize;
  shape: AvatarShape;
}

const AvatarGroupContext = React.createContext<AvatarGroupContextValue | null>(
  null,
);

function useAvatarGroupContext() {
  return React.useContext(AvatarGroupContext);
}

function getAvatarGroupMax(max: number | undefined): number | null {
  return typeof max === 'number' && Number.isFinite(max) && max > 0
    ? Math.floor(max)
    : null;
}

// ============================================================================
// Types
// ============================================================================

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

// Replace StatusIconType with IndicatorType
export type IndicatorType =
  | 'online'
  | 'away'
  | 'busy'
  | 'offline'
  | 'success'
  | 'verified'
  | 'upload'
  | 'edit';

// Add new types
export type AvatarShape = 'circle' | 'rounded';
export type AvatarType = 'image' | 'initials' | 'icon';
export type RingColor = 'primary' | 'success' | 'warning' | 'destructive';
export type IndicatorPosition = 'top-right' | 'bottom-right';
export type AvatarGroupVariant = 'default' | 'pill';
export type SoftColor =
  | 'primary'
  | 'indigo'
  | 'purple'
  | 'success'
  | 'warning'
  | 'slate';

// ============================================================================
// Variants
// ============================================================================

export const avatarVariants = cva('relative flex shrink-0', {
  variants: {
    size: {
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-14',
      '2xl': 'size-16',
    },
    shape: {
      circle: 'rounded-full',
      rounded: 'rounded-lg',
    },
  },
  defaultVariants: {
    size: 'sm',
    shape: 'rounded',
  },
});

// Ring classes use box-shadow geometry so image avatars do not lose crop area.
const ringColorMap: Record<RingColor, string> = {
  primary: 'ring-2 ring-ring',
  success: 'ring-2 ring-success',
  warning: 'ring-2 ring-warning',
  destructive: 'ring-2 ring-destructive',
};

// Soft color background and text
const softColorMap: Record<SoftColor, { bg: string; text: string }> = {
  primary: { bg: 'bg-primary/10', text: 'text-primary' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-500' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-500' },
  success: { bg: 'bg-success/10', text: 'text-success' },
  warning: { bg: 'bg-warning/10', text: 'text-warning' },
  slate: { bg: 'bg-slate-400/10', text: 'text-slate-400' },
};

// Default fallback colors use the Figma token surface.
const DEFAULT_FALLBACK_BG = 'bg-muted';
const DEFAULT_FALLBACK_TEXT = 'text-foreground';
const GROUP_FALLBACK_BG =
  'bg-[color-mix(in_srgb,var(--background)_97%,var(--foreground)_3%)]';

// ============================================================================
// Size Maps
// ============================================================================

// Font sizes for initials based on avatar size
const initialsSizeMap: Record<AvatarSize, string> = {
  xs: 'text-[10px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
  '2xl': 'text-xl',
};

const groupTextSizeMap: Record<AvatarSize, string> = {
  xs: 'text-xs leading-4',
  sm: 'text-xs leading-4',
  md: 'text-xs leading-4',
  lg: 'text-xs leading-4',
  xl: 'text-xs leading-4',
  '2xl': 'text-xs leading-4',
};

// Placeholder icon sizes based on avatar size
const placeholderIconSizeMap: Record<AvatarSize, string> = {
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-7',
  '2xl': 'size-8',
};

// Status indicator sizes based on avatar size
const statusIndicatorSizeMap: Record<AvatarSize, string> = {
  xs: 'size-2',
  sm: 'size-2.5',
  md: 'size-3',
  lg: 'size-3.5',
  xl: 'size-4',
  '2xl': 'size-5',
};

// Badge sizes for company/agent/verified indicators
const badgeSizeMap: Record<AvatarSize, string> = {
  xs: 'size-3.5',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-7',
  '2xl': 'size-8',
};

// Accessible labels for indicator types
const indicatorLabelMap: Record<IndicatorType, string> = {
  online: 'Online',
  away: 'Away',
  busy: 'Busy',
  offline: 'Offline',
  success: 'Success',
  verified: 'Verified',
  upload: 'Upload',
  edit: 'Edit',
};

// Badge icon sizes
const badgeIconSizeMap: Record<AvatarSize, string> = {
  xs: 'size-2',
  sm: 'size-2.5',
  md: 'size-3',
  lg: 'size-3.5',
  xl: 'size-4',
  '2xl': 'size-5',
};

// AvatarGroup uses a Figma-specific size ramp that sits between standalone
// Avatar sizes for xs and md.
const groupAvatarSizeMap: Record<AvatarSize, string> = {
  xs: 'size-7',
  sm: 'size-8',
  md: 'size-9',
  lg: 'size-12',
  xl: 'size-14',
  '2xl': 'size-16',
};

const groupAddIconSizeMap: Record<AvatarSize, string> = {
  xs: 'size-6',
  sm: 'size-6',
  md: 'size-6',
  lg: 'size-6',
  xl: 'size-7',
  '2xl': 'size-8',
};

// Spacing between avatars in group (negative margin)
const groupSpacingMap: Record<AvatarSize, string> = {
  xs: '-space-x-2.5',
  sm: '-space-x-3',
  md: '-space-x-3.5',
  lg: '-space-x-5',
  xl: '-space-x-6',
  '2xl': '-space-x-7',
};

// Pill variant padding based on avatar size
const pillPaddingMap: Record<AvatarSize, string> = {
  xs: 'px-0.5 py-0.5',
  sm: 'px-1 py-1',
  md: 'px-1 py-1',
  lg: 'p-1',
  xl: 'px-2 py-2',
  '2xl': 'px-2.5 py-2.5',
};

// Pill text padding based on avatar size
const pillTextPaddingMap: Record<AvatarSize, string> = {
  xs: 'px-1',
  sm: 'px-1.5',
  md: 'px-2',
  lg: 'px-2',
  xl: 'px-3',
  '2xl': 'px-3.5',
};

function getInitials(name?: string) {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

interface AvatarIndicatorProps {
  indicator?: IndicatorType;
  indicatorPosition: IndicatorPosition;
  sizeKey: AvatarSize;
}

function AvatarIndicator({
  indicator,
  indicatorPosition,
  sizeKey,
}: AvatarIndicatorProps) {
  if (!indicator) return null;

  const indicatorSize = statusIndicatorSizeMap[sizeKey];
  const badgeSize = badgeSizeMap[sizeKey];
  const badgeIconSize = badgeIconSizeMap[sizeKey];

  const dotPosition =
    indicatorPosition === 'top-right'
      ? 'absolute top-0 end-0'
      : 'absolute bottom-0 end-0';

  const badgePosition =
    indicatorPosition === 'top-right'
      ? 'absolute -top-1 -end-1'
      : 'absolute -bottom-0.5 -end-0.5';

  const statusDotColorMap: Record<string, string> = {
    online: 'bg-success',
    away: 'bg-warning',
    busy: 'bg-destructive',
    offline: 'bg-muted',
  };

  const dotColor = statusDotColorMap[indicator];
  if (dotColor) {
    return (
      <span
        role="status"
        aria-label={indicatorLabelMap[indicator]}
        className={cn(
          dotPosition,
          'block rounded-full border-2 border-background',
          dotColor,
          indicatorSize,
        )}
      />
    );
  }

  switch (indicator) {
    case 'success':
      return (
        <div
          role="img"
          aria-label={indicatorLabelMap[indicator]}
          className={cn(
            badgePosition,
            'flex items-center justify-center rounded-full bg-success',
            badgeSize,
          )}
        >
          <Check
            aria-hidden="true"
            className={cn('text-white stroke-[3]', badgeIconSize)}
          />
        </div>
      );

    case 'verified':
      return (
        <div
          role="img"
          aria-label={indicatorLabelMap[indicator]}
          className={cn(
            badgePosition,
            'flex items-center justify-center rounded-full bg-warning',
            badgeSize,
          )}
        >
          <Star
            aria-hidden="true"
            className={cn('text-white fill-current', badgeIconSize)}
          />
        </div>
      );

    case 'upload':
      return (
        <div
          role="img"
          aria-label={indicatorLabelMap[indicator]}
          className={cn(
            badgePosition,
            'flex items-center justify-center rounded-full bg-slate-400 border border-background',
            badgeSize,
          )}
        >
          <Plus
            aria-hidden="true"
            className={cn('text-white', badgeIconSize)}
          />
        </div>
      );

    case 'edit':
      return (
        <div
          role="img"
          aria-label={indicatorLabelMap[indicator]}
          className={cn(
            badgePosition,
            'flex items-center justify-center rounded-full bg-background border-2 border-border',
            badgeSize,
          )}
        >
          <Pencil
            aria-hidden="true"
            className={cn('text-foreground', badgeIconSize)}
          />
        </div>
      );

    default:
      return null;
  }
}

// ============================================================================
// Avatar Component
// ============================================================================

export interface AvatarProps {
  /** Avatar size */
  size?: AvatarSize;
  /** Avatar shape - rounded (default) or circle */
  shape?: AvatarShape;
  /** Avatar type - determines content rendering */
  type?: AvatarType;
  /** Ring outline - boolean uses primary, or specify color */
  ring?: boolean | RingColor;
  /** Indicator overlay */
  indicator?: IndicatorType;
  /** Position of the indicator - top-right (default) or bottom-right */
  indicatorPosition?: IndicatorPosition;
  /** Soft background color for icon type */
  softColor?: SoftColor;
  /** Image source URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Fallback text to generate initials from */
  fallback?: string;
  /** Custom icon for type="icon" */
  icon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  {
    src,
    alt,
    fallback,
    icon,
    size: sizeProp = 'sm',
    shape: shapeProp = 'rounded',
    type,
    ring,
    indicator,
    indicatorPosition = 'top-right',
    softColor,
    className,
  },
  ref,
) {
  const groupContext = useAvatarGroupContext();
  const [loadedSrc, setLoadedSrc] = React.useState<string>();
  const arrivedSrc = React.useRef<string>(undefined);
  const fallbackElement = React.useRef<HTMLSpanElement>(null);
  const [imageElement, setImageElement] =
    React.useState<HTMLImageElement | null>(null);
  React.useEffect(() => {
    const fallback = fallbackElement.current;
    if (!fallback) return;
    fallback.style.visibility = '';
    if (
      !imageElement?.isConnected ||
      !src ||
      imageElement.getAttribute('src') !== src
    )
      return;
    const settle = () => {
      arrivedSrc.current = src;
      fallback.style.visibility = 'hidden';
    };
    if (arrivedSrc.current === src) {
      settle();
      return;
    }
    const win = imageElement.ownerDocument.defaultView;
    if (!win) return;
    const preference = win.matchMedia('(prefers-reduced-motion: reduce)');
    if (preference.matches) {
      settle();
      return;
    }
    const styles = win.getComputedStyle(imageElement);
    const duration =
      styles.getPropertyValue('--duration-base').trim() || '160ms';
    const animation = imageElement.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration:
        Number.parseFloat(duration) * (duration.endsWith('ms') ? 1 : 1000),
      easing:
        styles.getPropertyValue('--ease-standard').trim() ||
        'cubic-bezier(0.2, 0, 0, 1)',
      fill: 'both',
    });
    // Motion bookkeeping stays outside React state, including native media
    // callbacks. Once settled, this avatar no longer observes preference flips.
    const finish = () => {
      settle();
      preference.removeEventListener('change', onPreferenceChange);
      animation.onfinish = null;
      animation.cancel();
    };
    const onPreferenceChange = (event: MediaQueryListEvent) => {
      if (event.matches) finish();
    };
    animation.onfinish = finish;
    preference.addEventListener('change', onPreferenceChange);
    return () => {
      preference.removeEventListener('change', onPreferenceChange);
      animation.onfinish = null;
      animation.cancel();
    };
  }, [imageElement, src]);
  const size = groupContext?.size ?? sizeProp;
  const shape = groupContext?.shape ?? shapeProp;
  const sizeKey = size || 'sm';

  // Determine avatar type from props
  const resolvedType: AvatarType =
    type ??
    (src ? 'image' : icon ? 'icon' : fallback || alt ? 'initials' : 'icon');

  // Get ring classes
  const getRingClasses = () => {
    if (!ring) return '';
    if (ring === true) return ringColorMap.primary;
    return ringColorMap[ring];
  };

  // Get shape classes for inner elements
  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';
  const sizeClasses = groupContext
    ? cn('relative flex shrink-0', groupAvatarSizeMap[sizeKey], shapeClass)
    : avatarVariants({ size, shape });

  // Get fallback background/text based on softColor or default
  const getFallbackStyles = () => {
    if (softColor) {
      return cn(softColorMap[softColor].bg, softColorMap[softColor].text);
    }
    return cn(
      groupContext ? GROUP_FALLBACK_BG : DEFAULT_FALLBACK_BG,
      DEFAULT_FALLBACK_TEXT,
    );
  };

  // Background for non-image avatars to prevent bleed-through in groups
  const getBackgroundClass = () => {
    if (resolvedType === 'image' && src) return '';
    if (softColor) return softColorMap[softColor].bg;
    return groupContext ? GROUP_FALLBACK_BG : DEFAULT_FALLBACK_BG;
  };

  return (
    <div
      ref={ref}
      data-slot="avatar"
      className={cn(
        'relative inline-block',
        sizeClasses,
        getBackgroundClass(),
        getRingClasses(),
        className,
      )}
    >
      <AvatarPrimitive.Root
        data-slot="avatar-root"
        data-size={size}
        className={cn(
          'relative flex size-full shrink-0 select-none overflow-hidden',
          shapeClass,
        )}
      >
        {resolvedType === 'image' && src && (
          <AvatarPrimitive.Image
            key={src}
            ref={setImageElement}
            data-slot="avatar-image"
            src={src}
            onLoadingStatusChange={(status) => {
              setLoadedSrc(status === 'loaded' ? src : undefined);
            }}
            alt={alt ?? ''}
            className={cn(
              'absolute inset-0 aspect-square size-full object-cover',
              shapeClass,
            )}
          />
        )}
        {/* Base UI owns image loading. Retain fallback bounds during arrival,
            then hide it so transparent portraits cannot reveal initials. */}
        <span
          ref={fallbackElement}
          data-slot="avatar-fallback"
          aria-hidden={
            resolvedType === 'image' && src && loadedSrc === src
              ? true
              : undefined
          }
          className={cn(
            'flex size-full items-center justify-center font-normal',
            shapeClass,
            getFallbackStyles(),
            initialsSizeMap[sizeKey],
          )}
        >
          {fallback || alt
            ? getInitials(fallback || alt)
            : (icon ?? (
                <User
                  className={cn(
                    softColor
                      ? softColorMap[softColor].text
                      : 'text-muted-foreground',
                    placeholderIconSizeMap[sizeKey],
                  )}
                />
              ))}
        </span>
      </AvatarPrimitive.Root>
      <AvatarIndicator
        indicator={indicator}
        indicatorPosition={indicatorPosition}
        sizeKey={sizeKey}
      />
    </div>
  );
});

Avatar.displayName = 'Avatar';

// ============================================================================
// AvatarGroup Component
// ============================================================================

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximum number of avatars to display before showing count */
  max?: number;
  /** Size of avatars in the group (lg by default) */
  size?: AvatarSize;
  /** Children (Avatar components) */
  children: React.ReactNode;
  /** Visual variant - default or pill (with container) */
  variant?: AvatarGroupVariant;
  /** Text template for pill variant, use {count} for overflow number */
  pillText?: string;
  /** Show the add more button */
  showAddButton?: boolean;
  /** Callback when add button is clicked */
  onAddClick?: () => void;
}

function getVisibleAvatarKey(
  child: React.ReactNode,
  position: number,
): React.Key {
  return React.isValidElement(child) && child.key !== null
    ? child.key
    : `avatar-slot-${position}`;
}

interface AvatarGroupPillTextProps {
  pillText?: string;
  remainingCount: number;
  sizeKey: AvatarSize;
}

function AvatarGroupPillText({
  pillText,
  remainingCount,
  sizeKey,
}: AvatarGroupPillTextProps) {
  if (!pillText) {
    return (
      <span className={cn('text-foreground', groupTextSizeMap[sizeKey])}>
        +{remainingCount}
      </span>
    );
  }

  const parts = pillText.split('{count}');
  return (
    <>
      {parts[0] && <span className="text-muted-foreground">{parts[0]}</span>}
      <span data-slot="avatar-group-pill-count" className="text-foreground">
        +{remainingCount}
      </span>
      {parts[1] && <span className="text-muted-foreground">{parts[1]}</span>}
    </>
  );
}

function AvatarGroup({
  max,
  size = 'lg',
  children,
  className,
  variant = 'default',
  pillText,
  showAddButton,
  onAddClick,
  ...props
}: AvatarGroupProps) {
  const childArray = React.Children.toArray(children);
  const safeMax = getAvatarGroupMax(max);
  const visibleChildren =
    safeMax === null ? childArray : childArray.slice(0, safeMax);
  const remainingCount =
    safeMax === null ? 0 : Math.max(0, childArray.length - safeMax);
  const sizeKey = size || 'lg';

  const contextValue = React.useMemo<AvatarGroupContextValue>(
    () => ({ size, shape: 'circle' }),
    [size],
  );

  const avatarsContent = (
    <div
      data-slot="avatar-group-stack"
      className={cn('flex items-center', groupSpacingMap[sizeKey])}
    >
      {visibleChildren.map((child, position) => (
        <div
          key={getVisibleAvatarKey(child, position)}
          className="ring-2 ring-background rounded-full"
          style={{ zIndex: position + 1 }}
        >
          {child}
        </div>
      ))}
      {remainingCount > 0 && variant === 'default' && (
        <div
          role="img"
          aria-label={`${remainingCount} more`}
          className={cn(
            'relative flex shrink-0 items-center justify-center rounded-full ring-2 ring-background',
            GROUP_FALLBACK_BG,
            groupAvatarSizeMap[sizeKey],
          )}
          style={{ zIndex: visibleChildren.length + 1 }}
        >
          <span
            aria-hidden="true"
            className={cn(
              'text-foreground font-normal',
              groupTextSizeMap[sizeKey],
            )}
          >
            +{remainingCount}
          </span>
        </div>
      )}
      {showAddButton && variant === 'default' && (
        <button
          type="button"
          onClick={onAddClick}
          aria-label="Add avatar"
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full ring-2 ring-background hover:bg-muted-foreground/10 transition-colors duration-(--duration-fast) ease-standard motion-reduce:transition-none',
            GROUP_FALLBACK_BG,
            groupAvatarSizeMap[sizeKey],
          )}
          style={{ zIndex: visibleChildren.length + 1 }}
        >
          <Plus
            className={cn(
              'text-muted-foreground',
              groupAddIconSizeMap[sizeKey],
            )}
          />
        </button>
      )}
    </div>
  );

  // Pill variant wrapper
  if (variant === 'pill') {
    return (
      <AvatarGroupContext.Provider value={contextValue}>
        <div
          data-slot="avatar-group"
          className={cn(
            'inline-flex items-center rounded-full bg-background border border-border shadow-sm',
            pillPaddingMap[sizeKey],
            className,
          )}
          {...props}
        >
          {avatarsContent}
          {remainingCount > 0 && (
            <span
              data-slot="avatar-group-pill-text"
              className={cn(
                pillTextPaddingMap[sizeKey],
                groupTextSizeMap[sizeKey],
              )}
            >
              <AvatarGroupPillText
                pillText={pillText}
                remainingCount={remainingCount}
                sizeKey={sizeKey}
              />
            </span>
          )}
        </div>
      </AvatarGroupContext.Provider>
    );
  }

  // Default variant
  return (
    <AvatarGroupContext.Provider value={contextValue}>
      <div
        data-slot="avatar-group"
        className={cn('inline-flex items-center', className)}
        {...props}
      >
        {avatarsContent}
      </div>
    </AvatarGroupContext.Provider>
  );
}

AvatarGroup.displayName = 'AvatarGroup';

// ============================================================================
// Exports
// ============================================================================

export { Avatar, AvatarGroup };
